// §11.2 — the `CommandProvider` seam, and the built-in provider behind it.
//
// §11.2 gives the interface outright:
//
//   ```ts
//   interface CommandProvider { id: string; commands: string[]
//     run(cmd: string, args: string[], cwd: string): AsyncIterable<ExecFrame> }
//   ```
//
// An `AsyncIterable` rather than a promise of output is the load-bearing part: `git clone` over
// Flightdeck's `exec` channel (M13.2) produces output for a minute, and a terminal that could only
// print when a command *finished* would be useless for the one job a terminal has. Every built-in
// yields too, even the ones that could return a string, because a provider that behaved differently
// from the seam would be a seam nobody tested.
//
// **Hangar never executes code server-side** (§11.2). Everything here runs against the VFS in the
// browser; anything else belongs to a provider an app registered.
import {
  basename,
  joinVfs,
  normalizeVfsPath,
  parentOf,
  type ExecFrame,
  type FileEntry,
  type VfsPath,
} from '@hangar/shared';

// §11.2's `ExecFrame` is `shared`'s, not a second copy: §9.3's `exec` channel carries the same
// shape, and M13.2 streams an installed app's frames straight into this terminal. Two definitions
// would be two chances for the wire and the built-ins to disagree about what output looks like.
export type { ExecFrame };

export interface CommandProvider {
  readonly id: string;
  readonly commands: readonly string[];
  run(cmd: string, args: readonly string[], cwd: VfsPath): AsyncIterable<ExecFrame>;
}

/** §11.2's unresolved-command string, exactly. */
export const commandNotFound = (cmd: string): string => `${cmd}: command not found`;

export const out = (text: string): ExecFrame => ({ kind: 'stdout', text });
export const err = (text: string): ExecFrame => ({ kind: 'stderr', text });
export const exit = (code: number): ExecFrame => ({ kind: 'exit', code });

/**
 * What the built-in provider is allowed to do.
 *
 * Narrow on purpose: the terminal is an app (§9.1) and reaches the world through `HangarAPI` like
 * any other. `cd` is the exception that proves it — it changes the *session's* state rather than
 * anything in the world, so it is handled by the shell around the provider rather than here.
 */
export interface TerminalHost {
  read(path: VfsPath): Promise<string>;
  write(path: VfsPath, text: string): Promise<void>;
  list(path: VfsPath): Promise<FileEntry[]>;
  mkdir(path: VfsPath): Promise<void>;
  remove(path: VfsPath, recursive: boolean): Promise<void>;
  move(from: VfsPath, to: VfsPath): Promise<void>;
  stat(path: VfsPath): Promise<FileEntry | null>;
  /** §11.2's Hangar verbs. */
  open(path: VfsPath): Promise<void>;
  install(url: string): Promise<void>;
  share(path: VfsPath): Promise<void>;
}

/** §7.1 — resolve a user-typed argument against the cwd, and refuse anything the grammar rejects. */
export function resolvePath(cwd: VfsPath, input: string): VfsPath {
  const raw = input.startsWith('/') ? input : joinVfs(cwd, input);
  // `joinVfs` does not collapse `..`, and a shell must: `cd ..` is the single most common thing
  // anyone types. Resolved here, then handed to the §7.1 grammar, which rejects what survives.
  const segments: string[] = [];
  for (const segment of raw.split('/')) {
    if (segment === '' || segment === '.') continue;
    if (segment === '..') {
      segments.pop();
      continue;
    }
    segments.push(segment);
  }
  const collapsed = `/${segments.join('/')}`;
  const result = normalizeVfsPath(collapsed);
  if (!result.ok) throw new Error(`invalid path: ${input}`);
  return result.path;
}

const BUILTIN_COMMANDS = [
  'ls',
  'dir',
  'cd',
  'pwd',
  'cat',
  'type',
  'mkdir',
  'rm',
  'cp',
  'mv',
  'echo',
  'clear',
  'help',
  'open',
  'install',
  'share',
] as const;

/** §11.2's help text. One line per command, in the order §11.2 lists them. */
const HELP: readonly (readonly [string, string])[] = [
  ['ls, dir [path]', 'list a directory'],
  ['cd [path]', 'change directory'],
  ['pwd', 'print the working directory'],
  ['cat, type <file>', 'print a file'],
  ['mkdir <path>', 'create a directory'],
  ['rm [-r] <path>', 'move to the Trash'],
  ['cp <from> <to>', 'copy a file'],
  ['mv <from> <to>', 'move or rename'],
  ['echo <text>', 'print text'],
  ['echo <text> > <file>', 'write text to a file'],
  ['clear', 'clear the screen'],
  ['open <path>', 'open in its default app'],
  ['install <url>', 'install an app from a manifest URL'],
  ['share <path>', 'share a file with another app'],
  ['help', 'this list'],
];

export function createBuiltinProvider(host: TerminalHost): CommandProvider {
  async function* run(
    cmd: string,
    args: readonly string[],
    cwd: VfsPath,
  ): AsyncIterable<ExecFrame> {
    try {
      switch (cmd) {
        case 'pwd':
          yield out(cwd);
          break;

        case 'ls':
        case 'dir': {
          const target = args[0] ? resolvePath(cwd, args[0]) : cwd;
          const entries = await host.list(target);
          // Directories first, then by name — the order every file manager uses, and the one
          // Explorer already shows, so the two do not disagree about the same folder.
          const ordered = [...entries].sort((a, b) =>
            a.isDir === b.isDir ? a.name.localeCompare(b.name) : a.isDir ? -1 : 1,
          );
          for (const entry of ordered) yield out(entry.isDir ? `${entry.name}/` : entry.name);
          break;
        }

        case 'cat':
        case 'type': {
          if (!args[0]) {
            yield err(`${cmd}: a file is required`);
            yield exit(1);
            return;
          }
          const target = resolvePath(cwd, args[0]);
          yield out(await host.read(target));
          break;
        }

        case 'mkdir': {
          if (!args[0]) {
            yield err('mkdir: a path is required');
            yield exit(1);
            return;
          }
          await host.mkdir(resolvePath(cwd, args[0]));
          break;
        }

        case 'rm': {
          const recursive = args.includes('-r') || args.includes('-rf');
          const rest = args.filter((arg) => !arg.startsWith('-'));
          if (!rest[0]) {
            yield err('rm: a path is required');
            yield exit(1);
            return;
          }
          // §7.8 — this is a move to the Trash, not an unlink. The only destructive operation in
          // the VFS is emptying it, and a shell is not allowed to be the exception.
          await host.remove(resolvePath(cwd, rest[0]), recursive);
          break;
        }

        case 'cp': {
          const [from, to] = args;
          if (!from || !to) {
            yield err('cp: usage: cp <from> <to>');
            yield exit(1);
            return;
          }
          const source = resolvePath(cwd, from);
          await host.write(await destinationFor(source, resolvePath(cwd, to)), await host.read(source));
          break;
        }

        case 'mv': {
          const [from, to] = args;
          if (!from || !to) {
            yield err('mv: usage: mv <from> <to>');
            yield exit(1);
            return;
          }
          const source = resolvePath(cwd, from);
          await host.move(source, await destinationFor(source, resolvePath(cwd, to)));
          break;
        }

        case 'echo': {
          // `echo text > file` — the one piece of shell syntax worth having, because without it
          // there is no way to create a file from the terminal at all.
          const redirect = args.indexOf('>');
          if (redirect === -1) {
            yield out(args.join(' '));
            break;
          }
          const target = args[redirect + 1];
          if (!target) {
            yield err('echo: a file is required after >');
            yield exit(1);
            return;
          }
          await host.write(resolvePath(cwd, target), `${args.slice(0, redirect).join(' ')}\n`);
          break;
        }

        case 'help':
          for (const [usage, description] of HELP) {
            yield out(`${usage.padEnd(24)}${description}`);
          }
          break;

        case 'open': {
          if (!args[0]) {
            yield err('open: a path is required');
            yield exit(1);
            return;
          }
          await host.open(resolvePath(cwd, args[0]));
          break;
        }

        case 'install': {
          if (!args[0]) {
            yield err('install: a manifest URL is required');
            yield exit(1);
            return;
          }
          await host.install(args[0]);
          break;
        }

        case 'share': {
          if (!args[0]) {
            yield err('share: a path is required');
            yield exit(1);
            return;
          }
          await host.share(resolvePath(cwd, args[0]));
          break;
        }

        // `cd` and `clear` change the session, not the world, so the shell around this handles
        // them. They are still *declared* here, because `commands` is what resolution consults
        // and a `cd` that fell through to another provider would be a surprising thing indeed.
        case 'cd':
        case 'clear':
          break;

        default:
          yield err(commandNotFound(cmd));
          yield exit(127);
          return;
      }
      yield exit(0);
    } catch (error) {
      yield err(`${cmd}: ${error instanceof Error ? error.message : String(error)}`);
      yield exit(1);
    }
  }

  /** `cp a.txt Documents` means *into* the directory, which is what everyone expects. */
  async function destinationFor(source: VfsPath, to: VfsPath): Promise<VfsPath> {
    const entry = await host.stat(to);
    return entry?.isDir === true ? joinVfs(to, basename(source)) : to;
  }

  return { id: 'builtin', commands: BUILTIN_COMMANDS, run };
}

export { parentOf };
